import React, { useEffect, useRef, useState } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import {
  DocumentEditorContainerComponent,
  Toolbar,
  Ribbon
} from "@syncfusion/ej2-react-documenteditor";
import { ThreeCircles } from "react-loader-spinner";

import { ContentBlockEditorModalProps } from "./ContentBlockEditorModal.utils";
import { syncfusionDocEditorServiceUrl } from "../../../../../constants/env";

import Switch from "../Switch/Switch";
import { fetchContentBlocks, saveContentBlockDetails } from "../../ContentBlocksPage.service";
import alertModalStyles from "../../../../../Reusables/components/AlertModal/AlertModal.module.css";
import CustomButton from "../../../../reusable_components/custom_button_component/Button";
import TextInput from "../../../../../Reusables/components/TextInput/TextInput";
import TextareaInput from "../../../../../Reusables/components/TextareaInput/TextareaInput";
import LabeledInputWrapper from "../../../../../Reusables/components/InputFieldsController/LabeledInputWrapper";
import styles from "./ContentBlockEditorModal.module.css";
import '../../../../../../node_modules/@syncfusion/ej2-base/styles/material.css';
import '../../../../../../node_modules/@syncfusion/ej2-buttons/styles/material.css';
import '../../../../../../node_modules/@syncfusion/ej2-inputs/styles/material.css';
import '../../../../../../node_modules/@syncfusion/ej2-popups/styles/material.css';
import '../../../../../../node_modules/@syncfusion/ej2-lists/styles/material.css';
import '../../../../../../node_modules/@syncfusion/ej2-navigations/styles/material.css';
import '../../../../../../node_modules/@syncfusion/ej2-splitbuttons/styles/material.css';
import '../../../../../../node_modules/@syncfusion/ej2-dropdowns/styles/material.css';
import "../../../../../../node_modules/@syncfusion/ej2-documenteditor/styles/material.css";
import "../../../../../../node_modules/@syncfusion/ej2-ribbon/styles/material.css";

DocumentEditorContainerComponent.Inject(Toolbar, Ribbon);

const INSTRUCTIONS_MAX_CHARS = 2000;

const ContentBlockEditorModal: React.FC<ContentBlockEditorModalProps> = ({
  blockName: initialBlockName,
  active: initialActive,
  status: initialStatus,
  mode,
  onHide,
  filePath,
  contentBlockId,
}) => {

  const editorRef = useRef<DocumentEditorContainerComponent>(null);

  const [blockName, setBlockName] = useState(initialBlockName);
  const [status, setStatus] = useState(initialStatus!);
  const [instructions, setInstructions] = useState("");
  const [saveStatus, setSaveStatus] = useState(false);
  const [isModalValuesEdited, setIsModalValuesEdited] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const isDisabled = mode === "view";
  const charsRemaining = INSTRUCTIONS_MAX_CHARS - instructions.length;
  const isNearLimit = charsRemaining <= 50;
  const isAtLimit = charsRemaining <= 0;

  useEffect(() => {
    (async () => {
      if (!contentBlockId || mode === "create") return;
      if (!editorRef.current) return;

      try {
        setShowLoader(true);
        const block = await fetchContentBlocks(contentBlockId);
        setShowLoader(false);
        if (block) {
          if (block?.Instructions) {
            setInstructions(block.Instructions);
          }

          const sfdt = block?.BlockData;
          if (sfdt) {
            editorRef.current.documentEditor.open(sfdt);
          }
        }
      } catch (err) {
        console.error("Failed loading content block:", err);
      }
    })();
  }, [contentBlockId]);

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve((reader.result as string).split(",")[1]);
      reader.readAsDataURL(blob);
    });
  };

  const handleOk = async () => {
    setSaveStatus(true);

    try {
      const editor = editorRef.current?.documentEditor;
      if (!editor) return;

      const docxBlob = await editor.saveAsBlob("Docx");
      const docxBase64 = await blobToBase64(docxBlob);

      const payload = {
        ContentBlockId: contentBlockId || 0,
        BlockName: blockName,
        BlockData: docxBase64,
        FilePath: filePath,
        Status: status,
        Active: initialActive,
        Instructions: instructions,
      };

      const response = await saveContentBlockDetails(payload);
      setSaveStatus(false);

      if (response) onHide(true);

    } catch (err) {
      console.error("Save Error:", err);
      setSaveStatus(false);
    }
  };

  const handleCancel = () => onHide(false);

  useEffect(() => {
    if (editorRef.current) {
      const editor = editorRef.current.documentEditor;
      editor.contentChange = () => {
        setIsModalValuesEdited(true);
      };
    }
  }, []);

  useEffect(() => {
    return () => {
      if (editorRef.current) {
        try {
          editorRef.current.documentEditor.destroy();
          editorRef.current.destroy();
        } catch (e) {
          console.warn("Failed to destroy editor", e);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    const editor = editorRef.current.documentEditor;
    if (mode === "view") {
      editor.isReadOnly = true;
      editorRef.current.enableToolbar = false;
      editorRef.current.showPropertiesPane = false;
    } else {
      editor.isReadOnly = false;
      editorRef.current.enableToolbar = true;
    }
  }, [mode]);

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.documentEditor.fitPage("FitPageWidth");
    editorRef.current.resize();

    if (mode === "view") return;

    const ribbon = editorRef.current.ribbon;
    ribbon.showTab("File", true);
    ribbon.showTab("Home", true);
    ribbon.showTab("Insert", true);
    ribbon.showTab("PageLayout", true);
    ribbon.showTab("References", false);
    ribbon.showTab("Review", false);
    ribbon.showTab("Mailings", false);
    ribbon.showTab("View", false);
    ribbon.showTab("Developer", false);
    ribbon.showTab("Protect", false);
  }, [editorRef]);

  return (
    <Modal
      onHide={onHide}
      show
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName={alertModalStyles.dialog}
      contentClassName={alertModalStyles.content}
      backdropClassName={alertModalStyles.backdrop}
    >
      <Modal.Header closeButton className={alertModalStyles.header}>
        <Modal.Title className={alertModalStyles.message}>
          {mode === "create" ? "Create Content Block" : blockName}
        </Modal.Title>

        <div className="flex-row-space-center w-50">
          <Switch
            isToggled={status === "Active"}
            onClick={() => {
              setIsModalValuesEdited(true);
              setStatus(status === "Active" ? "Inactive" : "Active");
            }}
            isDisabled={isDisabled}
          />
        </div>
      </Modal.Header>

      <Modal.Body className={`${alertModalStyles.body} ${styles.scrollableBody}`}>
        {showLoader &&
          <div className="disable-editor-overlay blurr-background" style={{ height: "90%" }}>
            <div className="flex-row-center-center" style={{ marginLeft: 5 }}>
              <div style={{ width: "fit-content", padding: 0 }}>
                <ThreeCircles
                  height="35"
                  width="35"
                  color="#4fa94d"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel="three-circles-rotating"
                  outerCircleColor="var(--theme1_tertiary)"
                  innerCircleColor="var(--theme1_secondary)"
                  middleCircleColor="var(--theme1_primary)"
                />
              </div>
              <h6 style={{ width: "fit-content", marginLeft: 10 }}>
                Loading your data
              </h6>
            </div>
          </div>
        }

        <Row className="mb-3">
          <Col md={4}>
            <TextInput
              field={{
                id: "blockName",
                type: "text",
                textStyle: { fontSize: 12, margins: { bottom: 5 } },
                margins: {top: 20, bottom: 0, left: 0, right: 0},
                placeholder: "Enter block name",
                label: "Block Name",
                value: blockName,
                disabled: isDisabled,
                show: true,
                validation: [{ type: "required", message: "Required!" }],
              }}
              handleChange={(fieldId, e) => {
                setIsModalValuesEdited(true);
                setBlockName(e.target.value);
              }}
              handleBlur={() => { }}
              handleSelect={() => { }}
            />
          </Col>
        </Row>

        {(
          <Row className="mb-3">
            <Col md={12}>
              <div className={styles.instructionsWrapper}>
                <div className={styles.instructionsHeader}>
                  <span className={`${styles.charCounter} ${isNearLimit ? styles.charCounterWarn : ""} ${isAtLimit ? styles.charCounterLimit : ""}`}>
                    {isAtLimit
                      ? "Limit reached"
                      : `${charsRemaining} character${charsRemaining !== 1 ? "s" : ""} remaining`}
                  </span>
                </div>

                <div className={styles.instructionsTextareaContainer}>
                  <TextareaInput
                    field={{
                      id: "instructions",
                      type: "textarea",
                      label: "Instructions",
                      placeholder: "Add usage instructions, notes, or guidelines for this content block…",
                      value: instructions,
                      disabled: isDisabled,
                      show: true,
                      textStyle: { fontSize: 12, margins: { bottom: 5 } },
                      margins: {top: 0, bottom: 0, left: 0, right: 0},
                      validation: [],
                    }}
                    handleChange={(fieldId, e) => {
                      const value = e.target.value.slice(0, INSTRUCTIONS_MAX_CHARS);
                      setInstructions(value);
                      setIsModalValuesEdited(true);
                    }}
                    handleBlur={() => {}}
                    handleSelect={() => {}}
                  />
                  <div className={`${styles.progressBar}`}>
                    <div
                      className={`${styles.progressFill} ${isNearLimit ? styles.progressWarn : ""} ${isAtLimit ? styles.progressLimit : ""}`}
                      style={{ width: `${((INSTRUCTIONS_MAX_CHARS - charsRemaining) / INSTRUCTIONS_MAX_CHARS) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}

        <Row>
          <Col md={12}>
            <LabeledInputWrapper
              field={{
                id: "",
                label: "Block Content",
                type: "text",
                value: "",
                textStyle: { fontSize: 12, margins: { bottom: 5 } },
                validation: [{ type: "required", message: "Required!" }],
              }}
            >
              <DocumentEditorContainerComponent
                id="sfdt-editor"
                ref={editorRef}
                height="300px"
                enableToolbar={mode !== "view"}
                toolbarMode="Ribbon"
                showPropertiesPane={false}
                enableTrackChanges={false}
                enableSpellCheck={false}
                serviceUrl={syncfusionDocEditorServiceUrl}
              />
            </LabeledInputWrapper>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer className={alertModalStyles.footer}>
        <CustomButton content="Cancel" type="quinary" onclick={handleCancel} />
        <CustomButton
          progress={saveStatus}
          disabled={isDisabled || !isModalValuesEdited || !blockName}
          content="Save"
          type="tertiary"
          onclick={handleOk}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ContentBlockEditorModal;